import {
  type AdapterSelectorConfig,
  IntegrationNotConfiguredError,
  requireIntegrationEnv,
} from "../../integrations/adapter-runtime";

export type AccountingProvider = "netsuite" | "quickbooks" | "sage";

export const ACCOUNTING_PROVIDER_ENV_VARS: Record<AccountingProvider, readonly string[]> = {
  quickbooks: ["QUICKBOOKS_CLIENT_ID", "QUICKBOOKS_CLIENT_SECRET", "QUICKBOOKS_REALM_ID"],
  netsuite: ["NETSUITE_ACCOUNT_ID", "NETSUITE_CONSUMER_KEY", "NETSUITE_CONSUMER_SECRET"],
  sage: ["SAGE_CLIENT_ID", "SAGE_CLIENT_SECRET"],
};

export interface AccountingTransactionInput {
  provider: AccountingProvider;
  sourceType: "invoice" | "settlement";
  sourceId: string;
  amountCents: number;
  pendingReviewAccountId: string;
  activeGlAccountId?: string;
  activeGlEnabled?: boolean;
}

export interface AccountingPushResult {
  provider: AccountingProvider;
  externalTransactionId: string;
  providerTraceId: string;
  accountId: string;
  status: "pending_review";
  raw: Record<string, unknown>;
}

export interface AccountingAdapter {
  provider: AccountingProvider;
  pushTransaction(input: Omit<AccountingTransactionInput, "provider">): Promise<AccountingPushResult>;
}

export function pushAccountingTransactionMock(input: AccountingTransactionInput): AccountingPushResult {
  const accountId = input.activeGlEnabled && input.activeGlAccountId
    ? input.activeGlAccountId
    : input.pendingReviewAccountId;
  return {
    provider: input.provider,
    externalTransactionId: `${input.provider}_${input.sourceType}_${input.sourceId}`,
    providerTraceId: `accounting_mock_${input.provider}_${input.sourceType}_${input.sourceId}`,
    accountId,
    status: "pending_review",
    raw: {
      mock: true,
      sourceType: input.sourceType,
      sourceId: input.sourceId,
      amountCents: input.amountCents,
      accountId,
    },
  };
}

export function createMockAccountingAdapter(provider: AccountingProvider): AccountingAdapter {
  return {
    provider,
    async pushTransaction(input) {
      return pushAccountingTransactionMock({ ...input, provider });
    },
  };
}

export function createAccountingProductionStub(
  config: AdapterSelectorConfig & { provider: AccountingProvider },
): AccountingAdapter {
  requireIntegrationEnv(config.env, "accounting", config.provider, ACCOUNTING_PROVIDER_ENV_VARS[config.provider]);

  return {
    provider: config.provider,
    async pushTransaction() {
      throw new IntegrationNotConfiguredError("accounting", config.provider, ACCOUNTING_PROVIDER_ENV_VARS[config.provider]);
    },
  };
}

export function selectAccountingAdapter(
  config: AdapterSelectorConfig & { provider?: AccountingProvider },
): AccountingAdapter {
  const provider = config.provider ?? "quickbooks";
  if (config.mode === "production") return createAccountingProductionStub({ ...config, provider });
  return createMockAccountingAdapter(provider);
}
